import React, { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { CustomDialog } from "../../../components/StyledComponents";
import { DialogActions, DialogContent, DialogTitle, Step, StepLabel, Stepper } from "@mui/material";
import {
  ControlpanelPrimaryButton,
  ControlpanelSecondaryButton,
  GrayIconButton,
} from "../../../components/StyledButton";
import CloseIcon from "@mui/icons-material/Close";
import AddBoxIcon from "@mui/icons-material/AddBox";
import { useTranslation } from "react-i18next";
import CustomCropImage from "../CustomCropImage/CustomCropImage";
import SearchIcon from "@mui/icons-material/Search";
import CustomInput from "../../../components/CustomInput";
import my_company_logo from "../../../assets/images/logo/my-company-logo.png";
import { toast } from "react-toastify";
import { addGroup } from "../../../axios/Group/GroupUrlApi";
import { useLoading } from "../../../context/LoadingContext";
import { useAuth } from "../../../context/AuthContext";
import AddUserToGroupDialog from "./AddUserToGroupDialog";
import AddUserToGroupWhenCreateGroupDialog from "./AddUserToGroupWhenCreateGroupDialog";
import EastIcon from "@mui/icons-material/East";
import EditIcon16pxSvgNew from "../../../assets/images/icons/EditIcon16pxSvgNew";

const AddUserGroupDialog = ({
  open,
  handleClose,
  handleRefreshTable,
  userData,
  getStatusAddNewGroup,
}) => {
  const { t } = useTranslation();
  const { profile } = useAuth();

  const [imageUrl, setImageUrl] = useState();
  const [isNewImageSelected, setIsNewImageSelected] = useState(false);
  const [prevImageUrl, setPrevImageUrl] = useState(my_company_logo);
  const [defaultLogo, setDefaultLogo] = useState(my_company_logo);
  const [show, hide] = useLoading();
  const [groupId, setGroupId] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const anchorRefAdvanced = useRef(null);

  const steps = [
    t("common.group-details"),
    t("common.add-users"),
  ];

  const [openSelectUserForUserGroup, setOpenSelectUserForUserGroup] =
    useState(false);

  const handleOpenSelectUserForUserGroup = () => {
    setOpenSelectUserForUserGroup(true);
  };

  const handleCloseSelectUserForUserGroup = () => {
    setActiveStep(0);
    handleCloseDialog();
  };

  const imageInputRef = useRef(null);

  const handleIconClick = () => {
    imageInputRef.current.click();
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setIsNewImageSelected(true);
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setPrevImageUrl(base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const [formData, setFormData] = useState({
    groupCode: "",
    groupDescription: "",
  });

  const handleCloseDialog = () => {
    handleClose();
    setFormData({
      groupCode: "",
      groupDescription: "",
    });
    setActiveStep(0);
    setPrevImageUrl(my_company_logo);
  };

  // Tour: điều khiển step 1 / step 2 mà không cần submit thật
  useEffect(() => {
    if (!open) return;

    window.__goAddUserGroupStep1 = () => {
      flushSync(() => {
        setActiveStep(0);
        setOpenSelectUserForUserGroup(false);
      });
    };
    window.__openAddUserGroupStep2 = () => {
      flushSync(() => {
        setActiveStep(1);
        setOpenSelectUserForUserGroup(true);
      });
    };
    window.__closeAddUserGroupStep2 = () => {
      flushSync(() => {
        setActiveStep(0);
        setOpenSelectUserForUserGroup(false);
      });
    };

    return () => {
      delete window.__goAddUserGroupStep1;
      delete window.__openAddUserGroupStep2;
      delete window.__closeAddUserGroupStep2;
    };
  }, [open]);

  // const handleInputChange = (e) => {
  //   const { name, value } = e.target;
  //   setFormData((prevData) => ({
  //     ...prevData,
  //     [name]: value,
  //   }));
  // };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    const limits = {
      groupCode: 64,
      groupDescription: 128,
    };

    const max = limits[name] ?? Infinity;
    const clipped = value.length > max ? value.slice(0, max) : value;

    setFormData((prev) => ({
      ...prev,
      [name]: clipped,
    }));
  };

  const resetProfilePicture = () => {
    setPrevImageUrl(my_company_logo);
  };

  const handleSubmit = async (e) => {
    let logo = null;
    if (defaultLogo !== prevImageUrl) {
      logo = prevImageUrl.includes(",")
        ? prevImageUrl.split(",")[1]
        : prevImageUrl;
    }
    const payload = {
      companyId: profile.companyId,
      profilePicture: logo,
      remark: formData.groupDescription,
      groupName: formData.groupCode,
      langId: localStorage.getItem("i18nextLngId"),
    };
    try {
      e.preventDefault();
      show();
      const response = await addGroup(payload);
      if (response.data.responseCode === 0) {
        setGroupId(response.data.data[0].id);
        toast.success(t("common.add-group-successfully"));

        handleRefreshTable();
        setActiveStep(1);
        getStatusAddNewGroup(true);
        setOpenSelectUserForUserGroup(true);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      if (error.response) {
        if (error.response.status !== 401) {
          toast.error(t(error?.response.data));
        }
      } else {
        toast.error(t('common.network-timeout'));
      }
    } finally {
      hide();
    }
  };

  return (
    <>
      <CustomDialog
        maxWidth={"xl"}
        open={open}
        onClose={handleCloseDialog}
        keepMounted
        transitionDuration={window.__tourIsOpen ? 0 : undefined}
        data-tour="user-group-dialog-step1"
      >
        <DialogTitle>
          <div className="flex justify-between">
            <div className="flex items-center gap-4">
              <p className="text-xl font-semibold ">
                {t("common.add-user-group")}
              </p>
            </div>
            <GrayIconButton onClick={handleCloseDialog}>
              <CloseIcon />
            </GrayIconButton>
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="mt-6 mb-6">
            <Stepper
              activeStep={activeStep}
              alternativeLabel
              sx={{
                "& .MuiStepLabel-label.Mui-active": {
                  color: "#0057FF",
                  fontWeight: 600,
                },

                "& .MuiStepLabel-label.Mui-completed": {
                  color: "#0057FF",
                  fontWeight: 600,
                },

                "& .MuiStepIcon-root.Mui-active": {
                  color: "#0057FF",
                },

                "& .MuiStepIcon-root.Mui-completed": {
                  color: "#0057FF",
                },
                "& .MuiStepConnector-root.Mui-active .MuiStepConnector-line, & .MuiStepConnector-root.Mui-completed .MuiStepConnector-line":
                {
                  borderColor: "#0057FF",
                },
              }}
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </div>
          {activeStep === 0 && (
            <div className="pt-5" data-tour="user-group-dialog-step1-input">
              <div className="flex flex-col">
                <div className="flex items-center justify-between">
                  <input
                    type="file"
                    ref={imageInputRef}
                    className="hidden"
                    onChange={handleImageChange}
                    accept="image/*"
                  />
                </div>

                <div className="flex items-center justify-center overflow-hidden p-[5px] rounded-[4px] bg-white">
                  <div className="relative p-1 border-[1px] border-[#E5E7EB] rounded inline-block self-end">
                    <img
                      src={prevImageUrl}
                      alt="picture_profile"
                      className="h-[170px] w-[168px]"
                    />
                    <button
                      ref={anchorRefAdvanced}
                      onClick={handleIconClick}
                      className="
                                              absolute
                                              bottom-0
                                              right-0
                                              w-10
                                              h-10
                                              rounded-xl
                                              bg-[#0057FF]
                                              hover:bg-[#00308C]
                                              flex
                                              items-center
                                              justify-center
                                              shadow-lg
                                              transition
                                          "
                    >
                      <EditIcon16pxSvgNew />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-x-10 gap-y-3 relative">
                  <div>
                    <CustomInput
                      required={true}
                      title={t("common.group-code")}
                      inputType="text"
                      name="groupCode"
                      id="editGroupCode"
                      placeholder={t("common.group-code")}
                      handleInputChange={handleInputChange}
                      value={formData.groupCode}
                      inputProps={{ maxLength: 64 }}
                    />
                    {/* <p className="text-sm text-gray-500 text-right mt-1">
                      {formData.groupCode.length}/64
                    </p> */}
                  </div>
                  <div>
                    <CustomInput
                      // required={true}
                      title={t("common.description")}
                      inputType="text"
                      name="groupDescription"
                      id="editGroupDescription"
                      placeholder={t("common.description")}
                      handleInputChange={handleInputChange}
                      value={formData.groupDescription}
                      inputProps={{ maxLength: 128 }}
                    />
                    {/* Bộ đếm ký tự */}
                    {/* <p className="text-sm text-gray-500 text-right mt-1">
                      {formData.groupDescription.length}/128
                    </p> */}
                  </div>
                </div>
              </div>
            </div>
          )}

        </DialogContent>
        <DialogActions>
          <div className="flex gap-2 px-4 py-4">
            <ControlpanelSecondaryButton onClick={handleCloseDialog}>
              <p>{t("button.close")}</p>
            </ControlpanelSecondaryButton>
            <div data-tour="user-group-dialog-step1-continue">
              <ControlpanelPrimaryButton
                disabled={!formData.groupCode && !window.__tourIsOpen}
                onClick={handleSubmit}
              >
                <p>{t("button.continue")}</p>
              </ControlpanelPrimaryButton>
            </div>
          </div>
        </DialogActions>
      </CustomDialog>
      {activeStep === 1 && (
        <AddUserToGroupWhenCreateGroupDialog
          activeStep={activeStep}
          steps={steps}
          onClose={handleCloseSelectUserForUserGroup}
          handleRefreshTable={handleRefreshTable}
          groupId={groupId}
          userData={userData}
          getStatusAddNewGroup={getStatusAddNewGroup}
          embedded
        />
      )}
    </>
  );
};

export default AddUserGroupDialog;
